import { useCallback } from 'react';
import { supabase, SUPABASE_URL } from '@/src/lib/supabase';

export interface InvitePeek { venue_name: string; role: string; email: string; status: string; expired: boolean; userExists: boolean }

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export function useOnboarding() {
  const provision = useCallback(async (venueName: string, name: string): Promise<{ error: string | null; venueId: string | null }> => {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return { error: 'not signed in', venueId: null };
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/provision-account`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ venue_name: venueName, name }),
    });
    const body = (await resp.json()) as { ok?: boolean; error?: string; venue_id?: string };
    if (!resp.ok || !body.ok) return { error: body.error ?? `http ${resp.status}`, venueId: null };
    return { error: null, venueId: body.venue_id ?? null };
  }, []);

  // Used by an already-authenticated user with no active venue — either
  // adding a second venue as an existing owner, or starting their first
  // venue from scratch.
  const createVenue = useCallback(async (name: string, cuisine?: string): Promise<{ error: string | null; venueId: string | null }> => {
    const { data, error } = await supabase.functions.invoke('venues', { body: { action: 'create', name } });
    if (error || !data?.ok) return { error: (data as { error?: string })?.error ?? error?.message ?? 'Could not create venue.', venueId: null };
    if (cuisine) {
      await supabase.from('venues').update({ cuisine }).eq('id', data.venue_id);
    }
    return { error: null, venueId: data.venue_id as string };
  }, []);

  const peekInvite = useCallback(async (token: string): Promise<InvitePeek | { error: string }> => {
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/accept-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}` },
      body: JSON.stringify({ token }),
    });
    const body = (await resp.json()) as { venue_name: string; role: string; email: string; status: string; expired: boolean; user_exists?: boolean; ok?: boolean; error?: string };
    if (!resp.ok || !body.ok) return { error: body.error ?? 'invite not found' };
    return { venue_name: body.venue_name, role: body.role, email: body.email, status: body.status, expired: body.expired, userExists: !!body.user_exists };
  }, []);

  const acceptInvite = useCallback(async (token: string): Promise<{ error: string | null }> => {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return { error: 'not signed in' };
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/accept-invite`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, accept: true }),
    });
    const body = (await resp.json()) as { ok?: boolean; error?: string };
    return { error: resp.ok && body.ok ? null : (body.error ?? `http ${resp.status}`) };
  }, []);

  return { provision, createVenue, peekInvite, acceptInvite };
}